import { DependencyList, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { normalizeError } from '@api/common/normalizeError';
import { type FetchOptions, requestHttp } from '@api/common/requestHttp';
import type { DataWithState } from '@api/models/common';
import type { ErrorResponse } from '../../../types/generated/pos';

export enum ResponseFormat {
	JSON = 'json',
	BLOB = 'blob',
}

export const useFetch = <OUTPUT>(
	url: string | null,
	options?: FetchOptions,
	deps?: DependencyList[number] | null,
	responseFormat?: `${ResponseFormat}`
): DataWithState<OUTPUT> => {
	const [data, setData] = useState<DataWithState<OUTPUT>>({
		data: null,
		loading: true,
		error: '',
	});

	useEffect(() => {
		if (!url) {
			return;
		}

		requestHttp(url, options)
			.then((response: Response) => {
				if (response.ok) {
					if (responseFormat === ResponseFormat.BLOB) {
						return response.blob() as Promise<OUTPUT>;
					}
					return response.json() as Promise<OUTPUT>;
				}
				throw new Error('Something went wrong.');
			})
			.then((parsedData) => {
				setData({ data: parsedData, loading: false, error: '' });
			})
			.catch((error) => {
				setData({ data: null, loading: false, error: error as string });
			});
	}, [url, deps]);

	return data;
};

type AsyncFn = (...args: unknown[]) => Promise<any>;
type MutateFn<ASYNC_FN extends AsyncFn> = (
	...args: Parameters<ASYNC_FN>
) => Promise<void>;
/**
 * Custom hook to call async function and handle loading and error states
 * Note: pass memoized function to avoid unnecessary re-renders
 */
export const useAsyncAction = <
	ASYNC_FN extends AsyncFn,
	OUTPUT = Awaited<ReturnType<ASYNC_FN>>,
>(
	asyncFn: ASYNC_FN
): [DataWithState<OUTPUT>, MutateFn<ASYNC_FN>] => {
	const [data, setData] = useState<DataWithState<OUTPUT>>({
		data: null,
		loading: false,
		error: '',
	});

	const fn = useCallback<MutateFn<ASYNC_FN>>(
		async (...args) => {
			setData({ data: data.data || null, loading: true, error: '' });
			try {
				const result: OUTPUT = await asyncFn(...args);
				setData({ data: result, loading: false, error: '' });
			} catch (error) {
				setData({ data: null, loading: false, error: error as string });
			}
		},
		[asyncFn]
	);

	return [data, fn];
};

type Request<INPUT> = (input: INPUT) => void;
export const useMutate = <OUTPUT, INPUT>(
	url: string | null,
	method: 'GET' | 'POST' = 'POST'
): [DataWithState<OUTPUT>, Request<INPUT>] => {
	const [data, setData] = useState<DataWithState<OUTPUT>>({
		data: null,
		loading: false,
		error: '',
	});

	const mutate = useCallback(
		(input: INPUT) => {
			if (!url) {
				return;
			}

			setData({ data: null, loading: true, error: '' });

			const options = {
				method,
				//TODO дописать логику передачи параметров в урле для гет-запросов
				body: JSON.stringify(input),
				headers: {
					'Content-Type': 'application/json',
					'X-Idempotence-Id': uuidv4(),
				},
			};

			requestHttp(url, options)
				.then(async (response: Response) => {
					const result = (await response.json()) as OUTPUT;

					if (response.ok) {
						setData({ data: result, loading: false, error: '' });
					} else {
						setData({
							data: null,
							loading: false,
							error: normalizeError(result),
						});
					}
				})
				.catch((error) => {
					setData({
						data: null,
						loading: false,
						error: {
							message: 'Something went wrong.',
							errorDetails: error as ErrorResponse['errorDetails'],
							errorType: 'UNKNOWN',
						},
					});
				});
		},
		[url, method]
	);

	return [data, mutate];
};
